/**
 * Coherencia país ↔ dirección (espejo ligero de backend/services/address_country_check.py).
 *
 * Incidente 27-ago-2026: un cliente de Factusol sin país y con "Francia" escrito
 * en la población se guardó como España y GLS generó una etiqueta nacional.
 * El backend bloquea (400); esto solo avisa antes en el formulario.
 */

const COUNTRY_NAME_HINTS: Record<string, string[]> = {
  ES: ['espana', 'spain', 'espagne', 'spanien', 'espanha'],
  PT: ['portugal'],
  FR: ['francia', 'france', 'frankreich', 'franca'],
  DE: ['alemania', 'germany', 'deutschland', 'allemagne', 'alemanha'],
  IT: ['italia', 'italy', 'italie', 'italien'],
  GB: ['reino unido', 'united kingdom', 'great britain', 'inglaterra', 'england', 'uk'],
  IE: ['irlanda', 'ireland', 'irlande'],
  BE: ['belgica', 'belgium', 'belgique', 'belgie', 'belgien'],
  NL: ['paises bajos', 'holanda', 'netherlands', 'nederland', 'holland', 'pays-bas'],
  LU: ['luxemburgo', 'luxembourg', 'luxemburg'],
  AT: ['austria', 'osterreich', 'autriche'],
  CH: ['suiza', 'switzerland', 'suisse', 'schweiz', 'svizzera'],
  AD: ['andorra', 'andorre'],
  PL: ['polonia', 'poland', 'polska', 'polen'],
  CZ: ['chequia', 'republica checa', 'czech republic', 'czechia', 'cesko'],
  SK: ['eslovaquia', 'slovakia', 'slovensko'],
  HU: ['hungria', 'hungary', 'magyarorszag'],
  RO: ['rumania', 'romania'],
  BG: ['bulgaria'],
  GR: ['grecia', 'greece', 'hellas'],
  HR: ['croacia', 'croatia', 'hrvatska'],
  SI: ['eslovenia', 'slovenia', 'slovenija'],
  DK: ['dinamarca', 'denmark', 'danmark'],
  SE: ['suecia', 'sweden', 'sverige'],
  FI: ['finlandia', 'finland', 'suomi'],
  NO: ['noruega', 'norway', 'norge'],
  EE: ['estonia', 'eesti'],
  LV: ['letonia', 'latvia', 'latvija'],
  LT: ['lituania', 'lithuania', 'lietuva'],
  MT: ['malta'],
  CY: ['chipre', 'cyprus'],
}

export const COUNTRY_LABELS: Record<string, string> = {
  ES: 'España', PT: 'Portugal', FR: 'Francia', DE: 'Alemania', IT: 'Italia', GB: 'Reino Unido',
  IE: 'Irlanda', BE: 'Bélgica', NL: 'Países Bajos', LU: 'Luxemburgo', AT: 'Austria', CH: 'Suiza',
  AD: 'Andorra', PL: 'Polonia', CZ: 'Chequia', SK: 'Eslovaquia', HU: 'Hungría', RO: 'Rumanía',
  BG: 'Bulgaria', GR: 'Grecia', HR: 'Croacia', SI: 'Eslovenia', DK: 'Dinamarca', SE: 'Suecia',
  FI: 'Finlandia', NO: 'Noruega', EE: 'Estonia', LV: 'Letonia', LT: 'Lituania', MT: 'Malta', CY: 'Chipre',
}

const POSTAL_CODE_PATTERNS: Record<string, RegExp> = {
  ES: /^(0[1-9]|[1-4]\d|5[0-2])\d{3}$/,
  PT: /^\d{4}(-\d{3})?$/,
  FR: /^\d{5}$/,
  DE: /^\d{5}$/,
  IT: /^\d{5}$/,
  AD: /^AD\d{3}$/i,
  BE: /^\d{4}$/,
  NL: /^\d{4}\s?[A-Z]{2}$/i,
  LU: /^(L-)?\d{4}$/i,
  AT: /^\d{4}$/,
  CH: /^\d{4}$/,
  DK: /^\d{4}$/,
  PL: /^\d{2}-?\d{3}$/,
  CZ: /^\d{3}\s?\d{2}$/,
  SK: /^\d{3}\s?\d{2}$/,
  HU: /^\d{4}$/,
  GR: /^\d{3}\s?\d{2}$/,
}

function normalize(text?: string | null): string {
  if (!text) return ''
  return text
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[()[\],./;:]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

export function countryLabel(iso?: string | null): string {
  const code = (iso || '').toUpperCase()
  return code ? `${COUNTRY_LABELS[code] || code} (${code})` : 'sin país'
}

/** País mencionado por nombre (palabra completa) en población/provincia; null si ninguno. */
export function inferCountryFromText(...texts: Array<string | null | undefined>): string | null {
  for (const text of texts) {
    const norm = normalize(text)
    if (!norm) continue
    for (const [iso, names] of Object.entries(COUNTRY_NAME_HINTS)) {
      for (const name of names) {
        const re = new RegExp(`(?<![a-z0-9])${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?![a-z0-9])`)
        if (re.test(norm)) return iso
      }
    }
  }
  return null
}

/** true/false si el CP encaja con el país; null si no sabemos validarlo. */
export function postalCodeMatches(country?: string | null, postalCode?: string | null): boolean | null {
  const iso = (country || '').toUpperCase()
  const cp = (postalCode || '').trim()
  if (!iso || !cp) return null
  const pattern = POSTAL_CODE_PATTERNS[iso]
  if (!pattern) return null
  return pattern.test(cp)
}

/** Primera incoherencia encontrada (texto listo para mostrar) o null si todo cuadra. */
export function getAddressCountryWarning(addr: {
  country?: string | null
  city?: string | null
  province?: string | null
  postal_code?: string | null
}): string | null {
  const iso = (addr.country || '').trim().toUpperCase()
  if (!iso) return 'La dirección no tiene país. Selecciona el país de destino.'

  const mentioned = inferCountryFromText(addr.city, addr.province)
  if (mentioned && mentioned !== iso) {
    return `La dirección menciona ${countryLabel(mentioned)} pero el país seleccionado es ${countryLabel(iso)}. Corrige el país antes de generar la etiqueta.`
  }
  if (postalCodeMatches(iso, addr.postal_code) === false) {
    return `El código postal '${(addr.postal_code || '').trim()}' no tiene el formato de ${countryLabel(iso)}.`
  }
  return null
}
